import { useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n/i18n";

const useSyncUserEmailStatus = (user, authInitialized) => {
	const { translate } = useTranslation();

	useEffect(() => {
		console.log("🧪 useSyncUserEmailStatus triggered", {
			authInitialized,
			userLoaded: !!user,
		});

		// Do not run until authInitialized is explicitly true or false
		if (authInitialized === undefined) {
			console.log("⏳ authInitialized is undefined, waiting...");
			return;
		}

		if (!authInitialized) {
			console.log("🚫 Auth has been initialized, but no user is logged in.");
			return;
		}

		if (!user) {
			console.log("🚫 No user available after auth init.");
			return;
		}

		const run = async () => {
			try {
				console.log("🔄 Reloading user to get latest auth data...");
				await user.reload();

				const ref = doc(db, "users", user.uid);
				console.log("📄 Fetching Firestore document:", ref.path);

				const snap = await getDoc(ref);
				if (!snap.exists()) {
					console.warn("⚠️ Firestore user document not found.");
					return;
				}

				const data = snap.data();
				const updates = {};

				// Email change
				if (data.email !== user.email) {
					updates.email = user.email;
					console.log(
						`✉️ Email mismatch detected. Firestore: ${data.email}, Auth: ${user.email}`
					);
				}

				// Email verification update
				if (data.emailVerified !== user.emailVerified) {
					updates.emailVerified = user.emailVerified;
					console.log(`✅ Email verification updated: ${user.emailVerified}`);
				}

				// Pending email resolution
				if (data.pendingEmail && data.pendingEmail === user.email) {
					updates.pendingEmail = null;
					updates.pendingEmailAt = null;
					updates.emailUpdatedAt = new Date().toISOString();
					toast.success(translate("alert.emailUpdatedSuccessfully"));
					console.log("🟢 Pending email resolved and marked as updated.");
				}

				// Apply updates if any
				if (Object.keys(updates).length > 0) {
					console.log("💾 Updating Firestore with:", updates);
					await setDoc(ref, updates, { merge: true });
					console.log("✅ Synced user email status.");
				} else {
					console.log("ℹ️ No update needed for user email status.");
				}
			} catch (err) {
				console.error("❌ Failed to sync user email status:", err);
			}
		};

		run();
	}, [user, authInitialized, translate]);
};

export default useSyncUserEmailStatus;
